import { Suspense,useMemo,useRef } from 'react'
import { useGLTF } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import type { ArenaDef } from './content'
import type { ScenarioDressingItem } from './arenaScenarioTypes'
import { SCENARIO_ASSET_URLS,scenarioProfileForArena } from './generatedArenaScenarios'
import { AssetBoundary } from './AssetBoundary'
import { scenicAssetFitScale } from './scenicFootprintBudget'
import { runtimePublicPath } from './runtimePublicPath'

function prepareDressing(scene:THREE.Object3D,item:ScenarioDressingItem){
 const model=scene.clone(true)
 const box=new THREE.Box3().setFromObject(model),size=box.getSize(new THREE.Vector3())
 const scale=scenicAssetFitScale(item.asset,'dressing',item.height,size)
 model.scale.setScalar(scale)
 const fitted=new THREE.Box3().setFromObject(model),center=fitted.getCenter(new THREE.Vector3())
 model.position.set(-center.x,-fitted.min.y,-center.z)
 model.traverse(node=>{
  if(!(node instanceof THREE.Mesh))return
  node.castShadow=true;node.receiveShadow=true
  if(node.material instanceof THREE.MeshStandardMaterial){
   const material=node.material.clone();material.envMapIntensity=Math.max(1.25,material.envMapIntensity);material.roughness=Math.max(.24,Math.min(.84,material.roughness))
   if(item.accent){material.emissive=new THREE.Color(item.accent);material.emissiveIntensity=.12}
   node.material=material
  }
 })
 return model
}
function DressingAsset({item}:{item:ScenarioDressingItem}){
 const{scene}=useGLTF(runtimePublicPath(SCENARIO_ASSET_URLS[item.asset]))
 const model=useMemo(()=>prepareDressing(scene,item),[scene,item])
 return <group position={item.position} rotation={[0,item.rotationY,0]} scale={item.scale}><primitive object={model}/></group>
}

export function ScenarioFxField({arena}:{arena:ArenaDef}){
 const profile=scenarioProfileForArena(arena.id),root=useRef<THREE.Group>(null)
 const geometry=useMemo(()=>{  const count=profile.fx.particleCount,data=new Float32Array(count*3),[sx,sy,sz]=profile.fx.particleSpread
  let seed=arena.id.split('').reduce((sum,char)=>sum+char.charCodeAt(0),17)
  const rand=()=>{seed=(seed*1664525+1013904223)>>>0;return seed/4294967296}
  for(let i=0;i<count;i++){const p=i*3;data[p]=(rand()-.5)*sx;data[p+1]=.35+rand()*sy;data[p+2]=-4-rand()*sz}
  const g=new THREE.BufferGeometry();g.setAttribute('position',new THREE.BufferAttribute(data,3));return g
 },[arena.id,profile.fx])
 useFrame(({clock},dt)=>{if(root.current){root.current.rotation.y+=dt*profile.fx.particleSpeed*.035;root.current.position.y=Math.sin(clock.elapsedTime*.28)*.035}})
 return <group ref={root}>
  <points geometry={geometry}><pointsMaterial color={arena.accent} size={.055} transparent opacity={.34} depthWrite={false} blending={THREE.AdditiveBlending}/></points>
  {profile.fx.haloRadii.map((radius,index)=><mesh key={radius} rotation={[-Math.PI/2,0,0]} position={[0,profile.fx.haloHeight+index*.004,-.2]}><ringGeometry args={[radius-.018,radius+.018,96]}/><meshBasicMaterial color={arena.accent} transparent opacity={.1-index*.018} blending={THREE.AdditiveBlending} depthWrite={false}/></mesh>)}  {profile.fx.beacons.map((position,index)=><group key={index} position={position}><pointLight color={arena.accent} intensity={profile.fx.beaconIntensity} distance={profile.fx.beaconDistance} decay={2}/><mesh><cylinderGeometry args={[.025,.12,4.2,12,1,true]}/><meshBasicMaterial color={arena.accent} transparent opacity={.09} blending={THREE.AdditiveBlending} depthWrite={false} side={THREE.DoubleSide}/></mesh></group>)}
 </group>
}

export function ScenarioDressing({arena,geometry=true,maxObjects}:{arena:ArenaDef;geometry?:boolean;maxObjects?:number}){
 const profile=scenarioProfileForArena(arena.id)
 return <group userData={{scenarioDressing:true,geometryAuthority:geometry}}>
  {geometry&&profile.dressing.slice(0,maxObjects??profile.dressing.length).map(item=><AssetBoundary key={item.id} label={`scenario dressing ${item.id}`}><Suspense fallback={null}><DressingAsset item={item}/></Suspense></AssetBoundary>)}
  <ScenarioFxField arena={arena}/>
 </group>
}
